/**
 * @author Ashish Dwivedi <ashish.dwivedi@314ecorp.com>
 * @description Invoice of past payments for subscription
 */
import { useState } from 'react';
import _ from 'lodash';
import { Divider, Flex, Tag, Tooltip } from 'antd';
import { Info } from '@icon-park/react';

import { AgGridTable, TABLE_OFFSET } from 'lib/agGrid';
import Button from 'components/widgets/button';
import useInvoiceHistory from 'hooks/admin/useInvoiceHistory';
import { getInvoiceAmountBreakdown, renderPlanName } from 'utils/admin/subscriptions';
import { changeDateFormat } from 'utils/dateParser';
import { STRIPE_PAYMENT_STATUSES } from 'app-constants/admin/subscriptions';
import { EMPTY_VALUE, SHORT_DATE_FORMAT, YEAR_MONTH_DATE_FORMAT } from '@veritable/common/constants';
import { ColDef, ICellRendererParams } from 'ag-grid-community';

const defaultPagination = { pageSize: 5, pageNumber: 1 };

/**
 * InvoiceTable Component
 */
const InvoiceTable: React.FC = () => {
	const [pageNumber, setPageNumber] = useState(defaultPagination.pageNumber);
	const [pageSize, setPageSize] = useState(defaultPagination.pageSize);
	const { data, isLoading, isError, refetch } = useInvoiceHistory(pageNumber, pageSize);

	const columns: ColDef<Subscriptions.InvoiceModel>[] = [
		{
			headerName: 'Date',
			field: 'issuing_date',
			minWidth: 150,
			cellRenderer: ({ data: record }: ICellRendererParams<Subscriptions.InvoiceModel>) =>
				changeDateFormat(record.issuing_date, YEAR_MONTH_DATE_FORMAT, SHORT_DATE_FORMAT),
		},
		{
			headerName: 'Plan Details',
			field: 'fees',
			cellRenderer: ({ data: record }: ICellRendererParams<Subscriptions.InvoiceModel>) => {
				const planDetail = _.find(record?.fees, (fee) => fee?.item?.type === 'subscription');
				return renderPlanName(planDetail?.plan_name);
			},
		},
		{
			headerName: 'Amount',
			field: 'fees',
			minWidth: 150,
			cellRenderer: ({ data: record }: ICellRendererParams<Subscriptions.InvoiceModel>) => {
				const breakdown = getInvoiceAmountBreakdown(record.fees, record.credit_notes_amount_cents);
				if (!breakdown) {
					return EMPTY_VALUE;
				}
				const tooltipContent = (
					<div style={{ minWidth: 220 }}>
						{_.map(breakdown.lineItems, (item) => (
							<Flex key={item.label} justify='space-between' gap={16}>
								<span>{item.label}</span>
								<span>${item.amount.toFixed(2)}</span>
							</Flex>
						))}
						<Divider style={{ margin: '6px 0', borderColor: '#4C4C4C' }} />
						<Flex justify='space-between' gap={16}>
							<strong>{'Total'}</strong>
							<strong>${breakdown.total.toFixed(2)}</strong>
						</Flex>
					</div>
				);
				const hasMultipleCategories = _.size(breakdown.lineItems) > 1;
				return (
					<Flex gap={6} align='center' style={{ display: 'inline-flex' }}>
						<span>${breakdown.total.toFixed(2)}</span>
						{hasMultipleCategories && (
							<Tooltip title={tooltipContent}>
								<Info style={{ cursor: 'pointer' }} />
							</Tooltip>
						)}
					</Flex>
				);
			},
		},
		{
			headerName: 'Transaction Count',
			minWidth: 180,
			cellRenderer: ({ data: record }: ICellRendererParams<Subscriptions.InvoiceModel>) => {
				const usageCount = _.find(record.fees, (fee) => fee?.item?.code === 'default_batch')?.units;
				if (_.isEmpty(usageCount)) {
					return EMPTY_VALUE;
				}
				return _.floor(_.toNumber(usageCount));
			},
		},
		{
			headerName: 'Status',
			field: 'payment_status',
			minWidth: 100,
			cellRenderer: ({ data: record }: ICellRendererParams<Subscriptions.InvoiceModel>) => {
				const tagMeta = _.find(STRIPE_PAYMENT_STATUSES, { value: record?.payment_status }) || STRIPE_PAYMENT_STATUSES.unknown;
				return <Tag color={tagMeta.color}>{tagMeta.label}</Tag>;
			},
		},
		{
			headerName: 'Actions',
			flex: 1,
			field: 'external_id',
			width: 200,
			minWidth: 200,
			cellRenderer: ({ data: record, node: { rowIndex } }: ICellRendererParams<Subscriptions.InvoiceModel>) => (
				<Button.DownloadInvoice isLatest={rowIndex === 0} invoice={record} />
			),
		},
	];

	const handlePageSizeChange = (newPageNumber: number, newPageSize: number) => {
		setPageNumber(newPageNumber);
		setPageSize(newPageSize);
	};

	const handlePageChange = (newPageNumber: number) => {
		setPageNumber(newPageNumber);
	};

	return (
		<AgGridTable
			headerFilter='Invoices' // Used as title here
			columnDefs={columns}
			rowData={data?.invoices}
			tableOffset={TABLE_OFFSET.invoices}
			loading={isLoading}
			isError={isError}
			reload={refetch}
			paginationProps={{
				total: data?.meta.total_count,
				pageSize,
				current: pageNumber,
				showSizeChanger: true,
				onChange: handlePageChange,
				onShowSizeChange: handlePageSizeChange,
			}}
		/>
	);
};

export { defaultPagination };
export default InvoiceTable;
